import apiClient from './api';

/**
 * Dashboard API service
 * Contains all API calls related to the dashboard
 */
const DashboardAPI = {
  /**
   * Fetches dashboard metrics data
   * @returns {Promise} Dashboard metrics including:
   * - summary (total_products, available_variants, low_stock_variants, incomplete_variants)
   * - incomplete_variants_list (details of incomplete variants)
   * - top_selling products
   * - recently_updated variants
   * - category_distribution
   * - recent_transactions
   */
  getDashboardMetrics: async () => {
    try {
      const response = await apiClient.get('/product/products/dashboard_metrics/');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  },

  /**
   * Fetches inventory pick list data
   * @param {Object} params - Optional query parameters
   * @param {string} params.marketplace - Filter by marketplace
   * @param {string} params.category - Filter by category
   * @param {string} params.location - Filter by location
   * @returns {Promise} Pick list data
   */
  getPickList: async (params = {}) => {
    try {
      const response = await apiClient.get('/product/inventory-transactions/pick_list/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching pick list:', error);
      throw error;
    }
  },

  /**
   * Marks a transaction as picked
   * @param {number} transactionId - ID of the transaction to mark as picked
   * @returns {Promise} Updated transaction data
   */
  markTransactionAsPicked: async (transactionId) => {
    try {
      const response = await apiClient.post(`/product/inventory-transactions/${transactionId}/mark_picked/`);
      return response.data;
    } catch (error) {
      console.error('Error marking transaction as picked:', error);
      throw error;
    }
  },

  /**
   * Fetches low stock items
   * @param {Object} params - Optional query parameters
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Low stock items
   */
  getLowStockItems: async (params = {}) => {
    try {
      // Filter variants with stock less than or equal to 0
      const response = await apiClient.get('/product/variants/', { 
        params: {
          ...params,
          ordering: 'stock' // Order by stock ascending
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching low stock items:', error);
      throw error;
    }
  },

  /**
   * Fetches incomplete variants (missing data or image)
   * @param {Object} params - Optional query parameters
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @returns {Promise} Incomplete variants
   */
  getIncompleteVariants: async (params = {}) => {
    try {
      // This is a simplified approach, the backend should ideally have a dedicated endpoint
      const response = await apiClient.get('/product/variants/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching incomplete variants:', error);
      throw error;
    }
  }
};

export default DashboardAPI;
